// components/LegalRepresentatives/RepresentativesPagination.js
/**
 * Composant de pagination pour les représentants légaux
 * @param {Object} props - Propriétés du composant
 * @param {Object} props.pagination - Informations de pagination
 * @param {Function} props.onPageChange - Fonction pour changer de page
 * @param {boolean} props.loading - État de chargement
 */
const RepresentativesPagination = ({ pagination, onPageChange, loading }) => {
  // Si pas de pagination ou une seule page, ne rien afficher
  if (!pagination || pagination.totalPages <= 1) return null;

  const { pageNumber, totalPages, hasPrevious, hasNext, totalCount } = pagination;

  /**
   * Générer les numéros de page à afficher
   * @returns {Array} Liste des numéros de page
   */
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, pageNumber - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    // Ajuster le début si on est près de la fin
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  /**
   * Changer de page
   * @param {number} newPage - Nouveau numéro de page
   */
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== pageNumber) {
      onPageChange(newPage);
    }
  };

  return (
    <nav aria-label="Pagination des représentants légaux">
      <ul className="pagination justify-content-center mb-0">
        
        {/* Bouton précédent */}
        <li className={`page-item ${!hasPrevious || loading ? 'disabled' : ''}`}>
          <button
            className="page-link"
            onClick={() => handlePageChange(pageNumber - 1)}
            disabled={!hasPrevious || loading}
            aria-label="Page précédente"
          >
            <i className="fas fa-chevron-left"></i>
          </button>
        </li>

        {/* Première page */}
        {getPageNumbers()[0] > 1 && (
          <>
            <li className="page-item">
              <button
                className="page-link"
                onClick={() => handlePageChange(1)}
                disabled={loading}
              >
                1
              </button>
            </li>
            {getPageNumbers()[0] > 2 && (
              <li className="page-item disabled">
                <span className="page-link">...</span>
              </li>
            )}
          </>
        )}

        {/* Pages numérotées */}
        {getPageNumbers().map(page => (
          <li
            key={page}
            className={`page-item ${page === pageNumber ? 'active' : ''}`}
          >
            <button
              className="page-link"
              onClick={() => handlePageChange(page)}
              disabled={loading}
              aria-current={page === pageNumber ? 'page' : undefined}
            >
              {page}
            </button>
          </li>
        ))}

        {/* Dernière page */}
        {getPageNumbers()[getPageNumbers().length - 1] < totalPages && (
          <>
            {getPageNumbers()[getPageNumbers().length - 1] < totalPages - 1 && (
              <li className="page-item disabled">
                <span className="page-link">...</span>
              </li>
            )}
            <li className="page-item">
              <button
                className="page-link"
                onClick={() => handlePageChange(totalPages)}
                disabled={loading}
              >
                {totalPages}
              </button>
            </li>
          </>
        )}

        {/* Bouton suivant */}
        <li className={`page-item ${!hasNext || loading ? 'disabled' : ''}`}>
          <button
            className="page-link"
            onClick={() => handlePageChange(pageNumber + 1)}
            disabled={!hasNext || loading}
            aria-label="Page suivante"
          >
            <i className="fas fa-chevron-right"></i>
          </button>
        </li>
      </ul>

      {/* Informations de pagination */}
      <div className="text-center mt-2">
        <small className="text-muted">
          Page {pageNumber} sur {totalPages} • 
          Total : {totalCount} représentant(s)
        </small>
      </div>
    </nav>
  );
};

export default RepresentativesPagination;